// Channel settings types for suppression rules and channel configuration
/**
 * Suppression rule field identifiers
 */
export var SuppressionRuleField;
(function (SuppressionRuleField) {
    // Email fields
    SuppressionRuleField["EMAIL_ADDRESS"] = "email.address";
    SuppressionRuleField["EMAIL_DOMAIN"] = "email.domain";
    SuppressionRuleField["EMAIL_SUBJECT"] = "email.subject";
    SuppressionRuleField["EMAIL_HAS_ATTACHMENTS"] = "email.hasAttachments";
    // Phone fields (SMS & Voice)
    SuppressionRuleField["PHONE_NUMBER"] = "phone.number";
    SuppressionRuleField["PHONE_COUNTRY_CODE"] = "phone.countryCode";
    // Message fields (Email & SMS)
    SuppressionRuleField["MESSAGE_CONTENT"] = "message.content";
    SuppressionRuleField["MESSAGE_CLASSIFICATION"] = "message.classification";
})(SuppressionRuleField || (SuppressionRuleField = {}));
/**
 * Suppression rule operators
 */
export var SuppressionRuleOperator;
(function (SuppressionRuleOperator) {
    SuppressionRuleOperator["EQUALS"] = "equals";
    SuppressionRuleOperator["NOT_EQUALS"] = "not_equals";
    SuppressionRuleOperator["STARTS_WITH"] = "starts_with";
    SuppressionRuleOperator["ENDS_WITH"] = "ends_with";
    SuppressionRuleOperator["CONTAINS"] = "contains";
    SuppressionRuleOperator["IN"] = "in";
})(SuppressionRuleOperator || (SuppressionRuleOperator = {}));
/**
 * Message classification types for ML-based filtering
 */
export var MessageClassification;
(function (MessageClassification) {
    MessageClassification["SPAM"] = "SPAM";
    MessageClassification["SOLICITATION"] = "SOLICITATION";
})(MessageClassification || (MessageClassification = {}));
/**
 * Get allowed fields for a specific channel
 */
export function getAllowedFieldsForChannel(channel) {
    switch (channel) {
        case 'EMAIL':
            return [
                SuppressionRuleField.EMAIL_ADDRESS,
                SuppressionRuleField.EMAIL_DOMAIN,
                SuppressionRuleField.EMAIL_SUBJECT,
                SuppressionRuleField.EMAIL_HAS_ATTACHMENTS,
                SuppressionRuleField.MESSAGE_CONTENT,
                SuppressionRuleField.MESSAGE_CLASSIFICATION,
            ];
        case 'SMS':
            return [
                SuppressionRuleField.PHONE_NUMBER,
                SuppressionRuleField.PHONE_COUNTRY_CODE,
                SuppressionRuleField.MESSAGE_CONTENT,
                SuppressionRuleField.MESSAGE_CLASSIFICATION,
            ];
        case 'CALL':
            return [
                SuppressionRuleField.PHONE_NUMBER,
                SuppressionRuleField.PHONE_COUNTRY_CODE,
            ];
        default:
            return [];
    }
}
/**
 * Get allowed operators for a specific field
 */
export function getAllowedOperatorsForField(field) {
    switch (field) {
        case SuppressionRuleField.EMAIL_HAS_ATTACHMENTS:
            return [SuppressionRuleOperator.EQUALS];
        case SuppressionRuleField.MESSAGE_CLASSIFICATION:
            return [SuppressionRuleOperator.IN];
        case SuppressionRuleField.PHONE_COUNTRY_CODE:
            return [
                SuppressionRuleOperator.EQUALS,
                SuppressionRuleOperator.NOT_EQUALS,
            ];
        default:
            // String fields get common string operators (not NOT_EQUALS)
            return [
                SuppressionRuleOperator.EQUALS,
                SuppressionRuleOperator.STARTS_WITH,
                SuppressionRuleOperator.ENDS_WITH,
                SuppressionRuleOperator.CONTAINS,
            ];
    }
}
export function validateSuppressionRule(rule, channel) {
    const errors = [];
    // Check if field is allowed for channel
    const allowedFields = getAllowedFieldsForChannel(channel);
    if (!allowedFields.includes(rule.field)) {
        errors.push(`Field "${rule.field}" is not allowed for channel "${channel}"`);
    }
    // Check if operator is allowed for field
    const allowedOperators = getAllowedOperatorsForField(rule.field);
    if (!allowedOperators.includes(rule.operator)) {
        errors.push(`Operator "${rule.operator}" is not allowed for field "${rule.field}"`);
    }
    // Validate value based on field type
    if (rule.field === SuppressionRuleField.EMAIL_HAS_ATTACHMENTS) {
        if (typeof rule.value !== 'boolean') {
            errors.push(`Value for "${rule.field}" must be a boolean`);
        }
    }
    else if (rule.field === SuppressionRuleField.MESSAGE_CLASSIFICATION) {
        if (!Array.isArray(rule.value)) {
            errors.push(`Value for "${rule.field}" must be an array`);
        }
        else {
            const validClassifications = Object.values(MessageClassification);
            const invalidValues = rule.value.filter(v => !validClassifications.includes(v));
            if (invalidValues.length > 0) {
                errors.push(`Invalid classification values: ${invalidValues.join(', ')}`);
            }
        }
    }
    else {
        // String fields
        if (rule.operator === SuppressionRuleOperator.IN) {
            if (!Array.isArray(rule.value)) {
                errors.push(`Value for operator "IN" must be an array`);
            }
        }
        else if (typeof rule.value !== 'string') {
            errors.push(`Value for "${rule.field}" must be a string`);
        }
        else if (rule.value.trim().length === 0) {
            errors.push(`Value for "${rule.field}" cannot be empty`);
        }
    }
    return {
        isValid: errors.length === 0,
        errors,
    };
}
//# sourceMappingURL=channel-settings.js.map